import { ownerScreenDecisionCount } from './owner-decisions';

/*
  Модель боковой навигации. Вынесена из AppShell ради тестов: порядок
  разделов и то, что вообще попадает в меню, — это продуктовое решение,
  и оно должно быть проверяемо без браузера.

  Владелец видит существующие входы поручений, контроля и результатов.
  Неиспользуемая отдельная страница проектов остаётся скрытой.

  Группы идут по частоте использования, а не по алфавиту:
  сначала ежедневные разделы, затем объекты и гости, деньги,
  поручения и контроль, личные дела владельца — порядок по решению владельца. Заголовок группы показывается в сайдбаре,
  поэтому каждый раздел обязан попадать ровно в одну группу.
*/

export interface NavGroup {
  /** Стабильный id — нужен тестам и ключам React. */
  id: string;
  /** Русская строка-ключ: переводится через t() при отрисовке. */
  title: string;
  paths: readonly string[];
  /** Свёрнута на desktop под «Ещё». */
  collapsed?: boolean;
}

/** Закрытые устаревшие экраны. */
export const TECHNICIAN_PATHS: readonly string[] = ['/projects'];

/** Прямой URL закрытого экрана. */
export function isTechnicianPath(pathname: string): boolean {
  const path = pathname.split('?')[0] || pathname;
  return TECHNICIAN_PATHS.some((base) => path === base || path.startsWith(base + '/'));
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'daily',
    title: 'Каждый день',
    paths: ['/', '/inbox', '/leads', '/bookings', '/calendar'],
  },
  {
    id: 'guests',
    title: 'Объекты и гости',
    paths: ['/objects', '/market-intelligence', '/cleaning', '/tm30', '/transfers'],
  },
  {
    id: 'money',
    title: 'Деньги',
    paths: ['/money', '/owners', '/team'],
  },
  {
    id: 'work',
    title: 'Поручения и контроль',
    paths: ['/workspace', '/work', '/runs', '/control-plane'],
  },
  {
    id: 'mine',
    title: 'Мои дела',
    paths: ['/tasks', '/notes', '/waiting', '/decisions', '/documents'],
  },
];

/** Порядок навигации в сайдбаре — плоский, по группам. */
export const SIDEBAR_PATHS: readonly string[] = NAV_GROUPS.flatMap((group) => group.paths);

/** На desktop видно всё, кроме свёрнутой группы «Ещё». */
export const DESKTOP_PRIMARY_PATHS: readonly string[] = NAV_GROUPS
  .filter((group) => !group.collapsed)
  .flatMap((group) => group.paths);

/*
  На телефоне внизу четыре раздела: то, чем пользуются в дороге.
  «Входящие» — самое частое действие с телефона, «Брони» — кто живёт
  и кто заезжает, «Заявки» — ответить новому клиенту. Остальное — в «Ещё».
*/
export const MOBILE_PRIMARY_PATHS: readonly string[] = ['/', '/inbox', '/bookings', '/leads'];

/** Разделы, которые не должны попадать в мобильное «Ещё». */
export const HIDE_ON_MOBILE_PATHS: readonly string[] = [];

export interface NavGroupItems<T> {
  id: string;
  title: string;
  items: T[];
  collapsed: boolean;
}

/**
 * Раскладывает доступные пункты по группам в заданном порядке.
 * Пункт, которого нет в NAV_GROUPS (или недоступный по роли), просто
 * не показывается — меню никогда не показывает пустую группу.
 */
export function groupNav<T extends { to: string }>(items: readonly T[]): NavGroupItems<T>[] {
  const byPath = new Map(items.map((item) => [item.to, item]));
  return NAV_GROUPS.flatMap((group) => {
    const grouped = group.paths.flatMap((path) => {
      const item = byPath.get(path);
      return item ? [item] : [];
    });
    return grouped.length > 0 ? [{ id: group.id, title: group.title, items: grouped, collapsed: Boolean(group.collapsed) }] : [];
  });
}

/** Пункты меню, не попавшие ни в одну группу (страховка от потери раздела). */
export function ungroupedPaths<T extends { to: string }>(items: readonly T[]): string[] {
  const known = new Set(SIDEBAR_PATHS);
  return items.filter((item) => !known.has(item.to)).map((item) => item.to);
}

export interface AttentionCounts {
  /** «Сегодня»: решения владельца. Полученные сообщения с ответственным считаются одной строкой. */
  today?: number;
  /** Решения/исключения Jack Inbox — та же read-модель /attention, что и «Сегодня». */
  inbox: number;
  /** Просроченные и сегодняшние задачи. */
  tasksDue: number;
  /** Заявки гостей, требующие внимания. */
  leads: number;
  /** Открытые ожидания (follow-up). */
  waiting: number;
  /** Активные решения без причины и влияния. */
  decisions: number;
}

export const EMPTY_COUNTS: AttentionCounts = {
  today: 0,
  inbox: 0,
  tasksDue: 0,
  leads: 0,
  waiting: 0,
  decisions: 0,
};

/**
 * Счётчик для пункта меню. Ноль и меньше — счётчика нет: пустой бейдж
 * читается как «всё сломано», а не как «ничего не ждёт».
 */
export function badgeFor(path: string, counts: AttentionCounts): number | null {
  const value = (() => {
    switch (path) {
      case '/':
        return counts.today ?? 0;
      case '/inbox':
        return counts.inbox;
      case '/tasks':
        return counts.tasksDue;
      case '/leads':
        return counts.leads;
      case '/waiting':
        return counts.waiting;
      case '/decisions':
        return counts.decisions;
      default:
        return 0;
    }
  })();
  return value > 0 ? value : null;
}

/**
 * Один источник для «N требуют внимания» на «Сегодня» и бейджей меню:
 * read-модель /attention. Полученные сообщения гостя — одна строка на
 * «Сегодня» и по карточке во «Входящих». Входящие также включают лиды,
 * новые задачи и запросы на подтверждение во всех полосах.
 */
type CountedItem = { source: string; from_own?: boolean; triage_owner?: string | null };
export function attentionCountsFrom(model: { requires_you: CountedItem[]; in_progress?: CountedItem[]; waiting?: CountedItem[] }): Pick<AttentionCounts, 'today' | 'inbox'> {
  return {
    today: ownerScreenDecisionCount(model.requires_you),
    inbox: [...model.requires_you, ...(model.in_progress ?? []), ...(model.waiting ?? [])].filter(item => isDefaultInboxItem(item)).length,
  };
}

/** Fired after an inbox mutation so the shell badges reload /attention. */
export const ATTENTION_CHANGED_EVENT = 'jack:attention-changed';

/** Owners behind a card (collapsed cards may have several, null = no owner). */
export function triageOwnersOf(item: { triage_keys?: string[]; triage_owner?: string | null; triage_owners?: Array<string | null> }): Array<string | null> {
  return item.triage_owners?.length ? item.triage_owners : [item.triage_owner ?? null];
}

/** Owner filter: a mixed card matches each of its owners and «без владельца» if any key has none. */
export function matchesOwnerFilter(item: { triage_keys?: string[]; triage_owner?: string | null; triage_owners?: Array<string | null> }, filter: string): boolean {
  if (filter === 'all') return true;
  if (!item.triage_keys?.length) return false;
  return triageOwnersOf(item).includes(filter === 'none' ? null : filter);
}

/** What «Входящие» shows by default: incoming items without own/personal chats (same rule as the list). */
export function isDefaultInboxItem(item: CountedItem, showOwn = false): boolean {
  return isIncomingAttention(item) && (showOwn || !item.from_own);
}

/** Incoming messages, leads, captured requests and actionable approvals share this filter. */
export function isIncomingAttention(item: { source: string }): boolean {
  return ['guest_inbox', 'lead', 'task_inbox', 'jack_inbox'].includes(item.source);
}

